import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";

/**
 * The investors list. Clicking a row opens THAT investor's board in a new tab.
 *
 * Until 2026-10-29 the board rendered underneath this list, which meant it was a thing you
 * glanced at: looking at a second investor threw the first one away, and the list itself was
 * pushed off screen by five columns of cards. An investor's board is a place you WORK, so it
 * is now its own page (pages/InvestorBoardPage) shaped like the sale pipeline, and this page
 * went back to being a list.
 *
 * All the matching, filtering and status machinery moved with it. Nothing here fetches a
 * listing any more.
 */

const LIMIT = 20;

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const th = { textAlign: "left", padding: "10px 14px", fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "#8a929b", borderBottom: "1px solid #e6e9ee", whiteSpace: "nowrap" };
const td = { padding: "11px 14px", fontSize: "13.5px", color: "#2b323b", borderBottom: "1px solid #eef1f4" };
const card = { backgroundColor: "#fff", borderRadius: 10, boxShadow: "0 1px 3px rgba(16,24,40,.08)", overflow: "hidden" };
const btn = (bg, fg = "#fff") => ({ padding: "7px 14px", fontSize: 13, fontWeight: 600, background: bg, color: fg, border: "none", borderRadius: 6, cursor: "pointer" });

export default function InvestorsListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
    if (search.trim()) qs.set("search", search.trim());
    authFetch(`/investors?${qs.toString()}`)
      .then((r) => {
        if (r && Array.isArray(r.investors)) { setRows(r.investors); setTotal(r.total || 0); }
        else setError((r && r.detail) || "Could not load investors.");
      })
      .catch(() => setError("Could not load investors."))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  /** window.open rather than navigate: a new TAB is the ask, and it leaves this list standing
   *  behind the board so you can open a second investor without losing the first. */
  const openBoard = (r) => {
    // A board with no active criteria has nothing to match against AND nowhere to write a
    // status (investor_matches.criteria_id is NOT NULL), so it can only ever be five empty
    // columns. Refusing the click and saying why beats opening a tab that explains nothing.
    if (!r.has_criteria) return;
    window.open(`/investors/${r.id}/board`, "_blank", "noopener");
  };

  const lastPage = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div style={{ minHeight: "100vh", background: "#f5f6f8" }}>
      <Header showLogout={true} />
      <div style={{ padding: 20, maxWidth: 1700, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#161b22" }}>Investors</h1>
          <button type="button" onClick={() => navigate("/investors/new")} style={btn("#1f6feb")}>
            + New Investor
          </button>
        </div>

        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search investors…"
          style={{ width: 280, padding: "8px 12px", fontSize: 13.5, border: "1px solid #d7dce3",
                   borderRadius: 6, marginBottom: 12 }}
        />

        <div style={card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Household</th>
                <th style={th}>Financing</th>
                <th style={th}>Status</th>
                <th style={th}>Updated</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={{ ...td, color: "#8a929b" }} colSpan={5}>Loading…</td></tr>
              ) : error ? (
                <tr><td style={{ ...td, color: "#a5281b" }} colSpan={5}>{error}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td style={{ ...td, color: "#8a929b" }} colSpan={5}>No investors yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => openBoard(r)}
                    title={r.has_criteria
                      ? "Open this investor's board in a new tab"
                      : "No active search criteria — add one before this investor can have a board"}
                    style={{ cursor: r.has_criteria ? "pointer" : "default",
                             background: r.has_criteria ? undefined : "#fbfbfc" }}>
                  <td style={{ ...td, fontWeight: 600,
                               color: r.has_criteria ? "#161b22" : "#8a929b" }}>
                    {r.household_name || `Investor #${r.id}`}
                    {r.has_criteria ? (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "#8a929b" }}>↗</span>
                    ) : (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600,
                                     color: "#8a4b00", background: "#fff4e5",
                                     border: "1px solid #ffcc80", borderRadius: 4,
                                     padding: "1px 6px" }}>
                        no search yet
                      </span>
                    )}
                  </td>
                  <td style={td}>{r.financing_required === true ? "Required" : r.financing_required === false ? "Cash" : "—"}</td>
                  <td style={td}>{r.status || "—"}</td>
                  <td style={td}>{fmtDate(r.updated_at)}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button type="button" style={btn("#eef1f4", "#2b323b")}
                            onClick={(e) => { e.stopPropagation(); navigate(`/investors/${r.id}/edit`); }}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && total > LIMIT && (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={btn("#eef1f4", "#2b323b")}>Prev</button>
            <span style={{ fontSize: 13, color: "#6b7580", alignSelf: "center" }}>Page {page} of {lastPage}</span>
            <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)} style={btn("#eef1f4", "#2b323b")}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
